/**
 * ResponseDetails Component (Step 2 + Step 5 + Step 7)
 *
 * Full response details with header, body content, and actions.
 * Displays model name, score, winner badge, action buttons, meta row, and response body.
 *
 * A11y Foundation: Semantic button elements, clear visual labels, logical order.
 * Future enhancements: Keyboard handlers, ARIA labels, focus indicators.
 *
 * Plan: docs/07-CC-response-full-detail-plan Steps 2, 5, & 7
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { logEvent, createTimer, getCorrelationId } from '../../../utils/telemetry';
import { getModelLogo } from '../../../utils/logoMapping';
import { formatModelDisplayName } from '../../../utils/modelDisplayName';
import { detectVisualElements } from '../../../utils/detectVisualElements';
import { useIsTruncated } from '../../../hooks/useIsTruncated';
import { VisualRenderer } from './VisualRenderer';
import { CategoryBadgesRow } from './CategoryBadgesRow';
import { CriteriaBadges, type CriteriaScores } from '../comparisons/CriteriaBadges';
import { ScoringRubricModal } from '../scoring/ScoringRubricModal';
import { isFeatureEnabled } from '../../../utils/featureFlags';

const normalizeLabel = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const buildCriteriaScores = (scoreMap?: { [criterion: string]: number } | null): Partial<CriteriaScores> | null => {
  if (!scoreMap) {
    return null;
  }

  const mapped: Partial<CriteriaScores> = {};

  for (const [criterion, value] of Object.entries(scoreMap)) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      continue;
    }

    mapped[criterion as keyof CriteriaScores] = value;
  }

  return Object.keys(mapped).length > 0 ? mapped : null;
};

export interface ResponseDetailsProps {
  modelId: string;
  providerId: string;
  modelDisplayName?: string | null;
  providerName?: string | null;
  score?: number;
  scores?: { [criterion: string]: number };
  isWinner?: boolean;
  isFavorite?: boolean;
  content?: string;
  prompt?: string;
  metadata?: {
    tokens?: number;
    latency?: number;
    cost?: number;
  };
  comparisonTitle?: string;
  comparisonId?: string;
  createdAt?: string;
  responseTime?: number;
  status?: 'pending' | 'completed' | 'failed';
  onCopy?: () => void;
  onShare?: () => void;
  onFavoriteToggle?: () => void;
  onOpenComparison?: (comparisonId: string) => void;
  // Navigation props
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  prevModelLogo?: string;
  nextModelLogo?: string;
  canNavigatePrev?: boolean;
  canNavigateNext?: boolean;
  // Animation state props (controlled from parent for desktop)
  showLogoTransition?: boolean;
  transitionLogo?: string | null;
}

export const ResponseDetails: React.FC<ResponseDetailsProps> = ({
  modelId,
  providerId,
  modelDisplayName: modelDisplayNameProp,
  providerName: providerNameProp,
  score,
  scores,
  isWinner = false,
  isFavorite = false,
  content,
  prompt,
  metadata,
  comparisonTitle,
  comparisonId,
  createdAt,
  responseTime,
  status,
  onCopy,
  onShare,
  onFavoriteToggle,
  onOpenComparison,
  onNavigatePrev,
  onNavigateNext,
  prevModelLogo,
  nextModelLogo,
  canNavigatePrev = false,
  canNavigateNext = false,
  showLogoTransition: showLogoTransitionProp,
  transitionLogo: transitionLogoProp,
}) => {
  const getStatusMeta = (statusVal: typeof status | undefined) => {
    switch (statusVal) {
      case 'completed':
        return { color: 'bg-green-400', label: 'Status: Completed' } as const;
      case 'pending':
        return { color: 'bg-yellow-300', label: 'Status: Pending' } as const;
      case 'failed':
        return { color: 'bg-red-400', label: 'Status: Failed' } as const;
      default:
        return { color: 'bg-slate-400', label: 'Status: Unknown' } as const;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  // Animation state for logo transition (local for mobile, controlled from parent for desktop)
  const [showLogoTransitionLocal, setShowLogoTransitionLocal] = useState(false);
  const [transitionLogoLocal, setTransitionLogoLocal] = useState<string | null>(null);
  const [showRubricModal, setShowRubricModal] = useState(false);
  // NEW_DESIGN_BETA graduated - features always enabled
  const newDesignEnabled = true;

  // Use parent-controlled state if provided (desktop), otherwise use local state (mobile)
  const showLogoTransition = showLogoTransitionProp ?? showLogoTransitionLocal;
  const transitionLogo = transitionLogoProp ?? transitionLogoLocal;
  // Ref for prompt text element to measure if truncated
  const promptTextRef = useRef<HTMLParagraphElement>(null);
  const isTruncated = useIsTruncated(prompt, 'line-clamp-4', promptTextRef);
  const fallbackModelDisplayName = formatModelDisplayName(modelId);
  const normalizedModelDisplayName =
    normalizeLabel(modelDisplayNameProp) ??
    fallbackModelDisplayName;
  const providerLabel =
    normalizeLabel(providerNameProp) ??
    normalizeLabel(providerId) ??
    modelId;
  const modelDisplayName = normalizedModelDisplayName;
  const modelLogoAltText = `${normalizedModelDisplayName} logo`;
  const criteriaScores = useMemo(() => buildCriteriaScores(scores), [scores]);
  const overallScore = useMemo(() => {
    if (typeof score === 'number' && Number.isFinite(score)) {
      return score;
    }

    if (scores) {
      const validScores = Object.values(scores).filter(
        (value) => typeof value === 'number' && Number.isFinite(value)
      );

      if (validScores.length > 0) {
        const averageScore = validScores.reduce((sum, value) => sum + value, 0) / validScores.length;
        return parseFloat(averageScore.toFixed(2));
      }
    }

    return null;
  }, [score, scores]);
  const shouldShowCriteriaBadges = !!criteriaScores || overallScore !== null;
  // Log content display event (Step 5)
  useEffect(() => {
    if (content) {
      const timer = createTimer();
      logEvent({
        op: 'display_content',
        component: 'ResponseDetails',
        success: true,
        contentLength: content.length,
        duration_ms: timer.end(),
      });
    }
  }, [content]);

  // Format age relative to now (Step 7 - from ResponseCard)
  const formatAge = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Format response time in seconds (Step 7 - from ResponseCard)
  const formatResponseTime = (ms?: number) => {
    if (!ms) return null;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const age = createdAt ? formatAge(createdAt) : null;
  const formattedResponseTime = formatResponseTime(responseTime);
  const modelLogoSrc = getModelLogo(modelId, providerLabel);

  // Swipe gesture handlers for mobile navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const SWIPE_THRESHOLD = 50; // Minimum distance for swipe
    const VERTICAL_THRESHOLD = 100; // Maximum vertical movement to prevent scrolling interference

    const xDistance = touchStart.x - touchEnd.x;
    const yDistance = Math.abs(touchStart.y - touchEnd.y);

    // Only trigger swipe if horizontal movement is significant and vertical movement is small
    if (yDistance > VERTICAL_THRESHOLD) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }

    const timer = createTimer();
    const correlationId = getCorrelationId();

    // Swipe left (x distance positive and large enough) -> Next
    if (xDistance > SWIPE_THRESHOLD && canNavigateNext && onNavigateNext) {
      // Show logo transition animation if logo is available
      if (nextModelLogo) {
        setTransitionLogo(nextModelLogo);
        setShowLogoTransition(true);

        // Wait for animation (500ms) before navigating
        setTimeout(() => {
          onNavigateNext();
          setShowLogoTransition(false);
          setTransitionLogo(null);
        }, 500);
      } else {
        // No logo available, navigate immediately
        onNavigateNext();
      }

      logEvent({
        op: 'swipe_next',
        component: 'ResponseDetails',
        correlationId,
        success: true,
        swipeDistance: Math.abs(xDistance),
        duration_ms: timer.end(),
      });
    }
    // Swipe right (x distance negative and large enough) -> Previous
    else if (xDistance < -SWIPE_THRESHOLD && canNavigatePrev && onNavigatePrev) {
      // Show logo transition animation if logo is available
      if (prevModelLogo) {
        setTransitionLogo(prevModelLogo);
        setShowLogoTransition(true);

        // Wait for animation (500ms) before navigating
        setTimeout(() => {
          onNavigatePrev();
          setShowLogoTransition(false);
          setTransitionLogo(null);
        }, 500);
      } else {
        // No logo available, navigate immediately
        onNavigatePrev();
      }

      logEvent({
        op: 'swipe_prev',
        component: 'ResponseDetails',
        correlationId,
        success: true,
        swipeDistance: Math.abs(xDistance),
        duration_ms: timer.end(),
      });
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Chevron navigation handlers with logo animation (for mobile swipe indicators)
  const handleChevronNavigatePrev = () => {
    if (!onNavigatePrev) return;

    // Show logo transition animation if logo is available
    if (prevModelLogo) {
      setTransitionLogoLocal(prevModelLogo);
      setShowLogoTransitionLocal(true);

      // Wait for animation (500ms) before navigating
      setTimeout(() => {
        onNavigatePrev();
        setShowLogoTransitionLocal(false);
        setTransitionLogoLocal(null);
      }, 500);
    } else {
      // No logo available, navigate immediately
      onNavigatePrev();
    }
  };

  const handleChevronNavigateNext = () => {
    if (!onNavigateNext) return;

    // Show logo transition animation if logo is available
    if (nextModelLogo) {
      setTransitionLogoLocal(nextModelLogo);
      setShowLogoTransitionLocal(true);

      // Wait for animation (500ms) before navigating
      setTimeout(() => {
        onNavigateNext();
        setShowLogoTransitionLocal(false);
        setTransitionLogoLocal(null);
      }, 500);
    } else {
      // No logo available, navigate immediately
      onNavigateNext();
    }
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header Section - Sticky positioned below main header */}
      <div className="sticky top-0 z-40 bg-slate-900/70 backdrop-blur-sm">
        <div className="px-6 py-4 border-b border-slate-700/50">
        {/* Model Info Row - Hidden on desktop, shown on mobile */}
        <div className="flex items-start justify-between gap-4 mb-4 md:hidden">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-1">
              {modelLogoSrc && (
                <div className="w-10 h-10 bg-slate-700/30 rounded-lg flex items-center justify-center p-1.5 flex-shrink-0">
                  <img
                    src={modelLogoSrc}
                    alt={modelLogoAltText}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-bold text-white">{modelDisplayName}</h1>
                {isWinner && (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-cyan-500/20 border border-cyan-400/40 rounded text-xs font-bold text-cyan-300">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>Winner</span>
                  </span>
                )}
              </div>
            </div>

            {/* Category Badges Row */}
            {shouldShowCriteriaBadges ? (
              <div className="mt-2">
                <CriteriaBadges
                  criteriaScores={criteriaScores ?? undefined}
                  size="default"
                  overallScore={overallScore}
                  showOverallTrophy={isWinner}
                />
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setShowRubricModal(true)}
                    className="text-xs text-slate-400 hover:text-cyan-300 transition-colors underline decoration-dotted"
                  >
                    How We Score Responses — Full Rubric
                  </button>
                </div>
              </div>
            ) : (
              scores && Object.keys(scores).length > 0 && (
                <div className="mt-2">
                  <CategoryBadgesRow scores={scores} size="xs" />
                </div>
              )
            )}
          </div>
        </div>

        {/* Meta Row */}
        {formattedResponseTime && (
          <div className="flex items-center space-x-2 text-xs">
            <span className="text-slate-400">Response time: {formattedResponseTime}</span>
          </div>
        )}
        </div>
      </div>

      {/* Prompt section - collapsible */}
      {prompt && (
        <div className="px-6 py-4 border-b border-slate-700/50 border-l-4 border-l-cyan-500">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Your Prompt
                </h2>
                {(createdAt || status) && (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                    {createdAt && <span>{formatDate(createdAt)}</span>}
                    {status && (
                      <div className="flex items-center gap-1.5 text-slate-300">
                        <span
                          aria-hidden="true"
                          className={`inline-block w-2 h-2 rounded-full ${getStatusMeta(status).color}`}
                        />
                        <span className="sr-only">{getStatusMeta(status).label}</span>
                        <span className="capitalize text-slate-400">
                          {status}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <p
            ref={promptTextRef}
            className={`text-slate-200 text-sm whitespace-pre-wrap ${!isPromptExpanded ? 'line-clamp-4' : ''}`}
          >
            {prompt}
          </p>
          {isTruncated && (
            <div className="mt-2 flex flex-wrap items-center justify-start gap-3">
              <button
                onClick={() => {
                  const newExpandedState = !isPromptExpanded;
                  setIsPromptExpanded(newExpandedState);
                  logEvent({
                    op: newExpandedState ? 'prompt_expanded' : 'prompt_collapsed',
                    component: 'ResponseDetails',
                    success: true
                  });
                }}
                className="text-sm text-cyan-300 hover:text-cyan-200 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-1"
                aria-expanded={isPromptExpanded}
                aria-label={isPromptExpanded ? 'Show less of the prompt' : 'Show more of the prompt'}
              >
                {isPromptExpanded ? 'Show less' : 'Show more'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Response Body Section (Step 5 + Phase 2: Safe Rendering) */}
      {content && (
        <main className="p-6">
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            {detectVisualElements(content).size > 0 ? (
              <VisualRenderer content={content} className="text-slate-200" />
            ) : (
              <pre className="whitespace-pre-wrap text-slate-200 text-sm leading-relaxed font-mono">
                {content}
              </pre>
            )}
          </div>

          {/* Metadata Chips (Step 5) */}
          {metadata && (metadata.tokens || metadata.latency || metadata.cost) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {metadata.tokens && !newDesignEnabled && (
                <span className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-full text-xs text-slate-300">
                  {metadata.tokens.toLocaleString()} tokens
                </span>
              )}
              {metadata.latency && (
                <span className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-full text-xs text-slate-300">
                  {metadata.latency}ms
                </span>
              )}
              {metadata.cost && (
                <span className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-full text-xs text-slate-300">
                  ${metadata.cost.toFixed(4)}
                </span>
              )}
            </div>
          )}
        </main>
      )}

      {/* Mobile Swipe Indicators (Mobile only - hidden on desktop) */}
      {(canNavigatePrev || canNavigateNext) && (
        <>
          {/* Left Swipe Indicator */}
          {canNavigatePrev && (
            <button
              onClick={handleChevronNavigatePrev}
              className="sm:hidden fixed left-0 top-1/2 -translate-y-1/2 z-10 p-4 opacity-20 hover:opacity-40 active:opacity-60 transition-opacity"
              style={{ animation: 'pulse-once 2s ease-in-out 3' }}
              aria-label="Previous response (swipe right)"
            >
              <svg className="w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Right Swipe Indicator */}
          {canNavigateNext && (
            <button
              onClick={handleChevronNavigateNext}
              className="sm:hidden fixed right-0 top-1/2 -translate-y-1/2 z-10 p-4 opacity-20 hover:opacity-40 active:opacity-60 transition-opacity"
              style={{ animation: 'pulse-once 2s ease-in-out 3' }}
              aria-label="Next response (swipe left)"
            >
              <svg className="w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </>
      )}

      {/* Logo Transition Overlay - Mobile only */}
      {showLogoTransition && transitionLogo && (
        <div
          className="sm:hidden fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            animation: 'logoBackdropFadeIn 150ms ease-out',
          }}
        >
          <div
            style={{
              animation: 'logoZoomIn 150ms ease-out forwards, logoFadeOut 100ms ease-in 400ms forwards',
            }}
          >
            {/* Large Model Logo */}
            <img
              src={transitionLogo}
              alt="Model logo"
              className="w-32 h-32 object-contain drop-shadow-2xl"
              style={{
                filter: 'drop-shadow(0 0 20px rgba(6, 182, 212, 0.3))',
              }}
            />
          </div>
        </div>
      )}

      {/* Logo Transition Overlay - Desktop only */}
      {showLogoTransition && transitionLogo && (
        <div
          className="hidden sm:flex fixed inset-0 z-50 items-center justify-center"
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            animation: 'logoBackdropFadeIn 150ms ease-out',
          }}
        >
          <div
            style={{
              animation: 'logoZoomIn 150ms ease-out forwards, logoFadeOut 100ms ease-in 400ms forwards',
            }}
          >
            {/* Large Model Logo - Desktop size */}
            <img
              src={transitionLogo}
              alt="Model logo"
              className="w-56 h-56 object-contain drop-shadow-2xl"
              style={{
                filter: 'drop-shadow(0 0 30px rgba(6, 182, 212, 0.4))',
              }}
            />
          </div>
        </div>
      )}

      <ScoringRubricModal isOpen={showRubricModal} onClose={() => setShowRubricModal(false)} />
    </div>
  );
};
